/** Conductor telegraphs, firing lanes and damage windows. The round's musical
 * clock drives these rules on both authorities, even when audio is muted. */
import type {RaceCourse,RaceVec3} from './raceCourse.js';
import type {RaceEncounterState} from './raceEncounter.js';
import type {RaceTarget} from './raceTargets.js';
import * as pmath from './pmath.js';
export interface RaceAttackZone {id:string;targetId:string;center:RaceVec3;radius:number}
export interface RaceBossAttack {id:number;zone:RaceAttackZone;warning:boolean;active:boolean;progress:number}
export const RACE_EMITTER_HP=180,RACE_CORE_HP=560;
const beatAt=(c:Pick<RaceCourse,'rhythm'>,time:number)=>Math.max(0,time)*(c.rhythm?.bpm??120)/60;
const phaseAt=(beat:number,length:number)=>((beat%length)+length)%length;
export function raceRelayPhase(course:Pick<RaceCourse,'rhythm'>,index:number,time:number){
 const beat=beatAt(course,time)-index*16/3,phase=phaseAt(beat,16);
 return {cycle:Math.floor(beat/16),phase,warning:phase<4,active:phase>=4&&phase<6,open:phase>=8&&phase<14,
  opensIn:(phase<8?8-phase:24-phase)*60/(course.rhythm?.bpm??120)};
}
export function raceCoreOpen(course:Pick<RaceCourse,'rhythm'>,time:number):boolean{return phaseAt(beatAt(course,time),16)>=8&&phaseAt(beatAt(course,time),16)<14;}
export function raceCoreZone(course:Pick<RaceCourse,'arena'|'rhythm'>,time:number):RaceAttackZone|undefined{
 const zones=course.arena?.zones.filter(z=>z.targetId===course.arena!.core)??[];
 return zones[Math.floor(beatAt(course,time)/16)%zones.length];
}
export function raceInsideAttackZone(zone:RaceAttackZone,position:readonly number[]):boolean{
 return Math.abs(position[1]-zone.center[1])<=4&&pmath.hypot(position[0]-zone.center[0],position[2]-zone.center[2])<=zone.radius;
}
/** Visible vulnerability is purely an attack/recovery phase, never proximity. */
export function raceBossVulnerable(course:Pick<RaceCourse,'arena'|'rhythm'>,target:RaceTarget,time:number,state:RaceEncounterState|undefined):boolean{
 const a=course.arena;if(!a||!state?.entered||state.core<=0)return false;
 if(target.kind==='core')return state.emitters.every(h=>h<=0)&&raceCoreOpen(course,time);
 const index=a.emitters.indexOf(target.id);
 return index>=0&&state.emitters[index]>0&&raceRelayPhase(course,index,time).open;
}
export function raceBossFiringZone(course:Pick<RaceCourse,'arena'|'rhythm'>,targetId:string,time:number):RaceAttackZone|undefined{
 return targetId===course.arena?.core?raceCoreZone(course,time):course.arena?.zones.find(z=>z.targetId===targetId);
}
export function raceBossAttacks(course:Pick<RaceCourse,'arena'|'rhythm'>,time:number,state:RaceEncounterState|undefined):RaceBossAttack[]{
 const a=course.arena;if(!a||!state?.entered||state.core<=0)return [];
 if(state.emitters.some(h=>h>0))return a.emitters.flatMap((id,i)=>{
  if(state.emitters[i]<=0)return [];
  const phase=raceRelayPhase(course,i,time),zone=a.zones.find(z=>z.targetId===id)!;
  return phase.warning||phase.active?[{id:phase.cycle*3+i,zone,warning:phase.warning,active:phase.active,progress:phase.phase/4}]:[];
 });
 const beat=beatAt(course,time),phase=phaseAt(beat,16),safe=raceCoreZone(course,time);
 return a.zones.filter(z=>z.targetId===a.core&&z!==safe).map(zone=>({id:1000000+Math.floor(beat/16),zone,warning:phase<4,active:phase>=4&&phase<6,progress:phase/4}));
}
/** Sweep both the attack clock and player movement: no tunnelling through a
 * telegraphed column between packets. Columns demand a dodge, not a tiny hop. */
export function hitRaceBossZone(course:RaceCourse,state:RaceEncounterState|undefined,from:RaceVec3,to:RaceVec3,start:number,end:number):number|null{
 if(!state?.entered||end<start)return null;
 const steps=Math.max(1,Math.ceil(Math.min(.5,end-start)/.016));
 for(let i=0;i<=steps;i++){
  const t=i/steps,p=from.map((v,k)=>v+(to[k]-v)*t),padding=.45+pmath.hypot(to[0]-from[0],to[1]-from[1],to[2]-from[2])/steps/2;
  for(const hit of raceBossAttacks(course,start+(end-start)*t,state)){
   if(!hit.active||hit.id<=(state.lastStrike??-1))continue;
   const z=hit.zone,height=p[1]-z.center[1];
   if(height>=-.5&&height<12&&pmath.hypot(p[0]-z.center[0],p[2]-z.center[2])<=z.radius+1.5+padding)return hit.id;
  }
 }
 return null;
}
